import { useNavigate } from 'react-router-dom'
import { useCallback, useMemo } from 'react'

import { resolveFeatureSettings } from './resolveFeatureSettings'
import { CheckTransactionEligibilityProps } from './types'

import { useGetFeaturesSettings } from '@/domain/settings/useCases'
import { useUserStore } from '@/store/useUserStore'

export const useCheckTransactionEligibility = ({
  feature,
  ...props
}: CheckTransactionEligibilityProps) => {
  const navigate = useNavigate()

  const hasIdentityVerified = useUserStore(
    (state) => state.userAuthenticated?.hasIdentityVerified,
  )
  const hasEmailVerified = useUserStore(
    (state) => state.userAuthenticated?.email_confirmed,
  )
  const hasPhoneNumber = useUserStore(
    (state) => state.userAuthenticated?.phone_number,
  )

  const { data: remoteSettings, isLoading: isLoadingRemoteSettings } =
    useGetFeaturesSettings(
      {
        feature,
      },
      {
        enabled: !!feature,
      },
    )

  const redirectToValidationsByFeature = () => {
    navigate(`/validations-by-feature?feature=${feature}`)
  }

  /**
   * If no feature is provided → all fields are required by default.
   * If a feature is provided but the settings haven't loaded yet → return undefined (loading state).
   * If a feature is provided and settings are loaded → use the values returned from the endpoint.
   */
  const effectiveSettings = useMemo(
    () => resolveFeatureSettings(feature, remoteSettings),
    [feature, remoteSettings],
  )

  const checkTransactionEligibility = useCallback(
    async (checkProps?: { amountInUSD?: number }): Promise<boolean> => {
      const amount = checkProps?.amountInUSD ?? 0

      if (!effectiveSettings || isLoadingRemoteSettings) {
        return false
      }

      if (
        !props?.ignorePhoneCheck &&
        effectiveSettings.isPhoneRequired &&
        !hasPhoneNumber
      ) {
        redirectToValidationsByFeature()
        return false
      }

      if (
        !props?.ignoreEmailCheck &&
        effectiveSettings.isEmailRequired &&
        !hasEmailVerified
      ) {
        redirectToValidationsByFeature()
        return false
      }

      if (
        !props?.ignoreIdentityCheck &&
        effectiveSettings.isKycRequired &&
        !hasIdentityVerified
      ) {
        const kycThreshold = effectiveSettings.kycRequiredAboveUSD

        const isBelowThreshold =
          kycThreshold !== undefined && amount < kycThreshold

        if (isBelowThreshold) {
          return true // Allowed without KYC
        }

        redirectToValidationsByFeature()
        return false
      }

      return true
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasPhoneNumber,
      hasEmailVerified,
      hasIdentityVerified,
      props?.ignorePhoneCheck,
      props?.ignoreEmailCheck,
      props?.ignoreIdentityCheck,
      effectiveSettings,
    ],
  )

  return {
    checkTransactionEligibility,
  }
}
